import type { LocalizedString } from "../i18n";
import { clientTypeAllowed } from "./client-types";
import type { CostOverrideMode } from "./cost-overrides";
import type { SnapshotInvalidation } from "./flow-instances";
import type { HistoricalStatistics } from "./historical-statistics";
import type {
  MandateBlockLevel,
  MandateMode,
  MandateOneTimeBasis,
  MandateStatus,
  MandateTemplateMembershipCollision,
} from "./mandate";
import type { ProductTypeAssessmentMode } from "./product-assessments";
import type { ProductSustainability } from "./sustainability";

// --- Collection ---

/** QP-164 — which entity drives expected-return/volatility/correlation calculations. */
export type PortfolioCollectionCalculationsMode = "asset_classes" | "products";

/** Wrapper-type configuration health, driving the warning icons on the collection tabs. */
export type WrapperTypeStatus = {
  /** The collection has at least one active wrapper type. */
  hasWrapperTypes: boolean;
  /**
   * Some products in the collection have no wrapper type. Always false while
   * hasWrapperTypes is false — the missing wrapper types are the root cause there.
   */
  hasProductsMissingWrapperTypes: boolean;
};

/**
 * Platform configuration health (QL-198), driving the warning on the Platforms
 * tab. Present only on the single-collection read, and only when the
 * collection has platforms.
 */
export type PlatformStatus = {
  /**
   * Platform row ids with no ACTIVE model portfolio tagged. Model-portfolio
   * resolution is platform-aware with explicit membership (QP-226 — no
   * untagged fallback), so no goal on such a platform can ever be advised a
   * model portfolio. Kept empty while the collection has no active model
   * portfolios at all — the missing portfolios are the root cause there
   * (mirrors the wrapperTypeStatus suppression rule).
   */
  platformIdsWithoutModelPortfolios: string[];
};

/**
 * QL-36 — goal-type availability health, driving the Goal Types tab warning.
 * Only meaningful when the collection configures goal types at all: a
 * collection with none is the legitimate pre-QP-187 setup and never warns.
 */
export type GoalTypeStatus = {
  /**
   * Client types left with no goal type to pick. Non-empty means a client of
   * that type reaches the goal form with an empty picker, and the form silently
   * falls back to name-only.
   */
  clientTypesWithoutGoalTypes: string[];
};

export type PortfolioCollection = {
  id: string;
  nameI18n: LocalizedString;
  descriptionI18n: LocalizedString | null;
  riskModelId: string | null;
  currency: string;
  riskFreeRatePct: number;
  /** Flat annual bank-savings rate assumed for the forecast bank-comparison series. */
  bankReturnPct: number;
  isActive: boolean;
  requiresAssetAllocation: boolean;
  requiresTimeseries: boolean;
  /** FS model the collection's platform conditions are defined against. Null = none. */
  financialSituationModelId: string | null;
  /** Hard-block flow completion while an allocation is below its product's minBuyAmount. */
  enforceMinBuyAmounts: boolean;
  /** QP-185 — opt-in gate for dynamic portfolios; off by default. */
  dynamicPortfoliosEnabled: boolean;
  /** QL-584 — dynamic generation moves only the missing sustainability share; only on while dynamic portfolios are. */
  dynamicPartialReplacementEnabled: boolean;
  /** Advisors and clients may pick a model portfolio from another risk score; requires a risk model. */
  allowModelPortfolioRiskDeviation: boolean;
  /** Opt-in gate for wrapper types; off by default. */
  wrapperTypesEnabled: boolean;
  /** QL-383 — gate for product types (tab, product field, picker filter/column); on by default. */
  productTypesEnabled: boolean;
  /** QP-164 — immutable while isActive. */
  calculationsMode: PortfolioCollectionCalculationsMode;
  /** QP-164 — Products-mode only: blocks activating a product without an ISIN. Ignored in Asset Classes mode. */
  requiresIsin: boolean;
  /** QL-375 — opt-in gate for mandates (templates, rules, components); off by default. Requires asset allocation. */
  mandatesEnabled: boolean;
  /** QL-375 — where a breach blocks: the goal's portfolios combined, or each portfolio on its own. */
  mandateBlockLevel: MandateBlockLevel;
  /** QL-375 — what a one-time portfolio is checked on; only differs in a follow-up. */
  mandateOneTimeBasis: MandateOneTimeBasis;
  /** QL-375 — the advisor may accept a breach on one-time investments with a note. */
  mandateAllowOneTimeDeviation: boolean;
  /** QL-375 — the same, for recurring instructions. */
  mandateAllowRecurringDeviation: boolean;
  /** QL-677 — advisors may make a custom portfolio the goal's mandate. */
  mandateAllowCreateFromPortfolio: boolean;
  /** Wrapper-type configuration health. Only present on the single-collection read, and only when wrapperTypesEnabled. */
  wrapperTypeStatus?: WrapperTypeStatus;
  /** QL-198 — platform configuration health. Only present on the single-collection read, and only when the collection has platforms. */
  platformStatus?: PlatformStatus;
  /** QL-36 — set only when the collection has at least one goal type. */
  goalTypeStatus?: GoalTypeStatus;
  /** QL-375 — set only when mandates are enabled. */
  mandateStatus?: MandateStatus;
  tenantId: string;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

export type PortfolioInCollection = {
  id: string;
  riskScore: number;
  nameI18n: LocalizedString;
  descriptionI18n: LocalizedString | null;
  /** QP-226 — platform row ids this portfolio is visible under. No entries = visible under no platform. */
  platformIds: string[];
  isActive: boolean;
  products: Array<{
    id: string;
    weight: number;
    product: {
      id: string;
      nameI18n: LocalizedString;
      isin: string | null;
    };
  }>;
};

export type PortfolioCollectionListItem = PortfolioCollection & {
  riskModel: {
    id: string;
    nameI18n: LocalizedString;
    matrixScores: { score: number }[];
  } | null;
  portfolios: PortfolioInCollection[];
};

export type CollectionSettings = {
  id: string;
  currency: string;
  riskFreeRatePct: number;
  bankReturnPct: number;
  requiresAssetAllocation: boolean;
  requiresTimeseries: boolean;
  /** FS model enabling client-status conditions on platforms. Null = none. */
  financialSituationModelId: string | null;
  enforceMinBuyAmounts: boolean;
  /** QP-185 — opt-in gate for dynamic portfolios; enabling requires a risk model. */
  dynamicPortfoliosEnabled: boolean;
  /** QL-584 — dynamic generation moves only the missing sustainability share; only on while dynamic portfolios are. */
  dynamicPartialReplacementEnabled: boolean;
  /** Advisors and clients may pick a model portfolio from another risk score; requires a risk model. */
  allowModelPortfolioRiskDeviation: boolean;
  /** Opt-in gate for wrapper types; disabling is blocked while products are tagged. */
  wrapperTypesEnabled: boolean;
  /** QL-383 — gate for product types; disabling is blocked while any product has a product type. */
  productTypesEnabled: boolean;
  /** QP-164 — immutable while isActive. */
  calculationsMode: PortfolioCollectionCalculationsMode;
  /** QP-164 — Products-mode only: blocks activating a product without an ISIN. Ignored in Asset Classes mode. */
  requiresIsin: boolean;
  /** QL-375 — read here; written through PATCH /:id/mandate-settings, not the settings endpoint. */
  mandatesEnabled: boolean;
  mandateBlockLevel: MandateBlockLevel;
  mandateOneTimeBasis: MandateOneTimeBasis;
  mandateAllowOneTimeDeviation: boolean;
  mandateAllowRecurringDeviation: boolean;
  mandateAllowCreateFromPortfolio: boolean;
  /** Set-once; null until assigned. Assignment goes through PATCH /:id, not the settings endpoint. */
  riskModelId: string | null;
  riskModel: { id: string; nameI18n: LocalizedString } | null;
};

/** POST /portfolio-collections. `currency` omitted = NOK; fixed after creation (QL-372). */
export type CreateCollectionRequest = {
  nameI18n: LocalizedString;
  descriptionI18n?: LocalizedString | null;
  riskModelId?: string;
  /** Three-letter upper-case ISO code. */
  currency?: string;
};

/** POST /portfolio-collections/from-template. `currency` omitted = the template's own. */
export type CreateCollectionFromTemplateRequest = {
  template: "standard";
  nameI18n?: LocalizedString;
  descriptionI18n?: LocalizedString | null;
  riskModelId?: string;
  /** Three-letter upper-case ISO code. */
  currency?: string;
};

/** PATCH /portfolio-collections/:id. */
export type UpdateCollectionRequest = {
  nameI18n?: LocalizedString;
  descriptionI18n?: LocalizedString | null;
  riskModelId?: string;
};

/** The currency is not part of this request: it is fixed at creation and a sent value is ignored (QL-372). */
export type UpdateCollectionSettingsRequest = {
  riskFreeRatePct: number;
  bankReturnPct: number;
  requiresAssetAllocation: boolean;
  requiresTimeseries: boolean;
  financialSituationModelId?: string | null;
  enforceMinBuyAmounts: boolean;
  /** QP-185 — enabling is rejected server-side while the collection has no risk model. */
  dynamicPortfoliosEnabled: boolean;
  /** QL-584 — enabling is rejected server-side while dynamic portfolios are off; reset to off when they are switched off. */
  dynamicPartialReplacementEnabled: boolean;
  /** Enabling is rejected server-side while the collection has no risk model. Omitted = unchanged. */
  allowModelPortfolioRiskDeviation?: boolean;
  /** Disabling is rejected server-side while any product in the collection is tagged to a wrapper type. */
  wrapperTypesEnabled: boolean;
  /** QL-383 — disabling is rejected server-side while any product in the collection has a product type. */
  productTypesEnabled: boolean;
  /** QP-164 — changing this is rejected server-side while the collection is active. */
  calculationsMode: PortfolioCollectionCalculationsMode;
  /** QP-164 — Products-mode only; enabling is rejected server-side while an active product has no ISIN. */
  requiresIsin: boolean;
};

// --- Currency Fees (FX conversion fees, percent; null = not configured) ---

export type CurrencyFeesResponse = {
  buyFxFeePct: number | null;
  sellFxFeePct: number | null;
};

export type UpdateCurrencyFeesRequest = {
  buyFxFeePct: number | null;
  sellFxFeePct: number | null;
};

export type CollectionRiskScoresResponse = {
  scores: number[];
};

// --- Portfolio ---

export type Portfolio = {
  id: string;
  portfolioCollectionId: string;
  riskScore: number;
  nameI18n: LocalizedString;
  descriptionI18n: LocalizedString | null;
  /** QP-226 — platform row ids this portfolio is visible under. No entries = visible under no platform. */
  platformIds: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CreatePortfolioRequest = {
  riskScore: number;
  nameI18n: LocalizedString;
  descriptionI18n?: LocalizedString | null;
  /** QP-226 — platform row ids the portfolio is visible under. Empty/omitted = visible under no platform. */
  platformIds?: string[];
};

export type UpdatePortfolioRequest = {
  nameI18n: LocalizedString;
  descriptionI18n?: LocalizedString | null;
  /** QP-226 — when provided, replaces the portfolio's platform membership. */
  platformIds?: string[];
};

export type PortfolioProductItem = {
  id: string;
  portfolioId: string;
  productId: string;
  product: {
    id: string;
    nameI18n: LocalizedString;
    isin: string | null;
    descriptionI18n: LocalizedString | null;
    /** Minimum buy per event (QP-193). Null = no minimum. */
    minBuyAmount?: number | null;
  };
  weight: number;
  order: number;
  createdAt: string;
  updatedAt: string;
};

export type PortfolioWithProducts = Portfolio & {
  products: PortfolioProductItem[];
  portfolioCollection: {
    id: string;
    nameI18n: LocalizedString;
    riskModelId: string | null;
    riskModel: { id: string; name: string } | null;
  };
};

// --- Dynamic Portfolio (QP-185, product weights since QL-584) ---

export type DynamicPortfolioProductItem = {
  id: string;
  productId: string;
  productNameI18n: LocalizedString;
  isin: string | null;
  weight: number;
  order: number;
};

export type DynamicPortfolio = {
  id: string;
  /** Matrix score of the collection's risk model, same keying as Portfolio.riskScore. */
  riskScore: number;
  /** Share (percent) one chosen client preference adds; 0 = no tilt. */
  maxPerPreference: number;
  /** Share (percent) all chosen client preferences add together; 0 = no tilt. */
  maxTotalPreferences: number;
  /** Platform row ids the portfolio is assigned to. Empty = serves goals without a platform. */
  platformIds: string[];
  /**
   * The base composition, in position order. May be empty or total less than
   * 100 while it is being set up; only a total within 0.01 of 100 is generated.
   */
  products: DynamicPortfolioProductItem[];
};

export type CreateDynamicPortfolioRequest = {
  riskScore: number;
  /** The complete platform membership; omitted or empty = no platform. */
  platformIds?: string[];
  /**
   * Whole percent, 0 to 50, not above `maxTotalPreferences`. The two maximums
   * are always sent together; omitted, both are 0.
   */
  maxPerPreference?: number;
  /** Whole percent, 0 to 50. */
  maxTotalPreferences?: number;
  /**
   * The products it starts with; omitted or empty = none. When one is refused
   * the dynamic portfolio is not created either.
   */
  products?: AddDynamicPortfolioProductRequest[];
};

/**
 * The risk score is fixed at creation. A request changes the platform
 * membership, the two preference maximums, or both; what is omitted stays.
 */
export type UpdateDynamicPortfolioRequest = {
  /** The complete platform membership; empty = no platform. */
  platformIds?: string[];
  /**
   * Whole percent, 0 to 50, not above `maxTotalPreferences`. The two maximums
   * are always sent together.
   */
  maxPerPreference?: number;
  /** Whole percent, 0 to 50. */
  maxTotalPreferences?: number;
};

export type AddDynamicPortfolioProductRequest = {
  productId: string;
  weight: number;
};

export type UpdateDynamicPortfolioProductRequest = {
  weight: number;
};

export type DynamicPortfolioProductMutationResponse = { success: true };

// --- Product ---

export type ProductCostValue = {
  id: string;
  costTypeId: string;
  value: number;
  calculatedValue: number;
  overriddenValue: number | null;
};

export type ProductAssetAllocation = {
  id: string;
  assetClassId: string;
  weight: number;
  assetClass: {
    id: string;
    nameI18n: LocalizedString;
  };
};

export type OrderAmountType = "amount" | "units";

export type Product = {
  id: string;
  portfolioCollectionId: string;
  nameI18n: LocalizedString;
  isin: string | null;
  descriptionI18n: LocalizedString | null;
  ticker: string | null;
  dataset: string | null;
  patchTicker: string | null;
  patchDataset: string | null;
  isActive: boolean;
  order: number;
  // How the product is ordered. "units" requires a valid instrument last price
  // and cannot be added to model portfolios. Immutable after creation.
  orderAmountType: OrderAmountType;
  productTypeId: string | null;
  productCostClassId: string | null;
  minTimeHorizonId: string | null;
  minRiskLevelId: string | null;
  /** QL-10 — minimum goal risk score required for suitability. Null = not configured. 0 is a real score. */
  minRiskScore: number | null;
  /** QP-226 — platform row ids this product is available under. No entries = available under no platform. */
  platformIds: string[];
  /** Wrapper type ids the product may be held in, preference-ordered (index 0 = preferred). */
  wrapperTypeIds: string[];
  /** QP-179 — per-criterion sustainability values (null = none entered). */
  sustainability?: ProductSustainability | null;
  /**
   * Minimum buy per event (one-time buy or each recurring occurrence), in the
   * collection currency. Null = no minimum. Never set on "units" products.
   */
  minBuyAmount: number | null;
  /** QP-164 — calculation inputs used when the collection's calculationsMode is "products". Null = not entered. */
  expectedReturn: number | null;
  expectedVolatility: number | null;
  costValues: ProductCostValue[];
  assetAllocations: ProductAssetAllocation[];
  /** QP-224 — per-preference boolean values; missing key ≡ false. */
  clientPreferences: ProductClientPreferenceValues;
  createdAt: string;
  updatedAt: string;
};

export type ProductCostValueInput = { calculatedValue: number; overriddenValue: number | null };

export type CreateProductRequest = {
  nameI18n: LocalizedString;
  isin?: string;
  descriptionI18n?: LocalizedString | null;
  ticker?: string;
  dataset?: string;
  patchTicker?: string;
  patchDataset?: string;
  /**
   * QL-11 — omitted on create means "not specified": the collection's default
   * product type is applied when it has one. An explicitly empty value ("" or
   * null) means "no type" and is never overwritten by the default. On update the
   * default is never applied — clearing a product's type is permanent.
   */
  productTypeId?: string | null;
  productCostClassId?: string;
  minTimeHorizonId?: string;
  minRiskLevelId?: string;
  /** QL-10 — minimum goal risk score. Omitted/null clears the value. 0 is a real score. */
  minRiskScore?: number | null;
  /** QP-226 — explicit platform membership. Empty/omitted = available under no platform. */
  platformIds?: string[];
  /** Wrapper type ids the product may be held in, preference-ordered. Undefined = leave untouched. */
  wrapperTypeIds?: string[];
  /** QP-179 — per-criterion sustainability values. Normalized against the collection config. */
  sustainability?: ProductSustainability | null;
  minBuyAmount?: number | null;
  /** QP-164 — calculation inputs used when the collection's calculationsMode is "products". Volatility can never be negative. */
  expectedReturn?: number | null;
  expectedVolatility?: number | null;
  orderAmountType?: OrderAmountType;
  costValues?: Record<string, ProductCostValueInput>;
  assetAllocations?: Array<{ assetClassId: string; weight: number }>;
  /** QP-224 — per-preference boolean values; missing key ≡ false, undefined = untouched. */
  clientPreferences?: ProductClientPreferenceValues;
};

export type UpdateProductRequest = CreateProductRequest;

/**
 * Sync health of the instrument backing a product, from timeseries_meta.
 * Lets product views show that the underlying data feed needs fixing.
 */
export type InstrumentSyncHealth = {
  /** Consecutive failed sync attempts (reset on success). */
  failures: number;
  /** Set when sync is disabled (auto after repeated failures, or by admin). */
  disabledAt: string | null;
  /** Last sync error message, if any. */
  error: string | null;
  /** Date of the newest synced data point (YYYY-MM-DD), null if never synced. */
  lastUpdate: string | null;
};

/** Traffic-light level for an instrument's data feed. */
export type InstrumentSyncLevel = "ok" | "warn" | "error";

/** A data point older than this many days makes the feed read as stale/`warn`. */
export const INSTRUMENT_SYNC_STALE_DAYS = 10;

/**
 * Pure classification of an instrument's sync health, shared by the API
 * (which blocks activating a product on a warn/error feed) and the web UI
 * (dot + alert colour). `error` = sync disabled; `warn` = failing attempts or
 * stale data; `ok` = healthy. `nowMs` is passed in so the function stays pure.
 */
export function instrumentSyncLevel(
  health: InstrumentSyncHealth,
  nowMs: number,
): InstrumentSyncLevel {
  if (health.disabledAt) return "error";
  if (health.failures > 0) return "warn";
  if (health.lastUpdate) {
    const ageDays = Math.floor(
      (nowMs - new Date(`${health.lastUpdate}T00:00:00Z`).getTime()) / 86_400_000,
    );
    if (ageDays > INSTRUMENT_SYNC_STALE_DAYS) return "warn";
  }
  return "ok";
}

export type ProductListItem = {
  id: string;
  nameI18n: LocalizedString;
  isin: string | null;
  ticker: string | null;
  dataset: string | null;
  isActive: boolean;
  orderAmountType: OrderAmountType;
  productTypeId: string | null;
  productCostClassId: string | null;
  minTimeHorizonId: string | null;
  minRiskLevelId: string | null;
  /** QL-10 — minimum goal risk score required for suitability. Null = not configured. */
  minRiskScore: number | null;
  /** QP-226 — platform row ids this product is available under. No entries = available under no platform. */
  platformIds: string[];
  /** Wrapper type ids the product may be held in, preference-ordered (index 0 = preferred). */
  wrapperTypeIds: string[];
  /** Minimum buy per event. Null = no minimum. Never set on "units" products. */
  minBuyAmount: number | null;
  costValues: ProductCostValue[];
  /** null when the product is not linked to an instrument. */
  instrumentSync: InstrumentSyncHealth | null;
};

/** QP-221 — one ranked product selection within an asset class. `id` is the selection row id; `productId` is the product. */
export type ProductSelectionItem = {
  id: string;
  productId: string;
  nameI18n: LocalizedString;
  isin: string | null;
  isActive: boolean;
  rank: number;
};

/** QP-221 — move one product to an absolute target rank; the server shifts the rows in between. */
export type ReorderSelectionRequest = {
  productId: string;
  newRank: number;
};

export type SuitabilityOptions = {
  /** QL-383 — the collection's "Use product types" setting. False = feature off (productTypes is then always empty). */
  productTypesEnabled: boolean;
  /** Active product types only; empty while product types are disabled. `isDefault` marks the collection's default (at most one). */
  productTypes: Array<{ id: string; nameI18n: LocalizedString; isDefault: boolean }>;
  timeHorizons: Array<{ id: string; labelI18n: LocalizedString; order: number }>;
  riskLevels: Array<{ id: string; labelI18n: LocalizedString; order: number }>;
  /** QL-10 — valid min-risk-score range from the collection risk model's matrix. Null = no risk model / empty matrix. */
  scoreRange: { min: number; max: number } | null;
  /** QL-10 — distinct score values of the collection risk model's matrix, ascending. Empty = no risk model / empty matrix. */
  matrixScores: number[];
};

// Morningstar X-Ray for a single product at 100% weight. Cached per ISIN on
// the client, so it is fetched/typed independently of the stats/timeseries.
export type ProductXray = {
  assetAllocation: { assetClass: string; percentage: number }[];
  countryExposure: { countryCode: string; percentage: number }[];
  regionalExposure: { region: string; percentage: number }[];
  globalStockSector: { sector: string; percentage: number }[];
  styleBox: { style: string; percentage: number }[];
  globalBondSector: { sector: string; percentage: number }[];
  topHoldings: {
    name: string;
    isin: string;
    country: string;
    sector: string;
    weight: number;
  }[];
  coverage: { totalProducts: number; coveredProducts: number; originalWeightPct: number };
  timestamp: string;
};

// X-Ray endpoint response; xray is null when the product has no ISIN or
// Morningstar rejected/failed the request.
export type ProductXrayResponse = {
  xray: ProductXray | null;
};

export type ProductAnalysisResponse = {
  // Expected return/volatility and historical return series; null/empty when the
  // product is not linked to an instrument or has insufficient price history.
  statistics: { expectedReturn: number; expectedVolatility: number } | null;
  timeseries: { date: string; value: number }[];
  // Report currency the timeseries and statistics are CONVERTED INTO — this is
  // the portfolio collection's currency, not the instrument's.
  currencyIso: string;
  // The instrument's own NATIVE (quote) currency, from its meta. May differ from
  // currencyIso (then an FX conversion is applied). null for instruments with no
  // meta currency (e.g. combined instruments).
  instrumentCurrency: string | null;
  // Latest captured price as a single datapoint, in the instrument's NATIVE
  // currency (which may differ from currencyIso). null when the instrument has
  // no captured price (e.g. categories, combined instruments).
  lastPrice: { value: number; currency: string | null; date: string | null } | null;
  // Sync health of the backing instrument — null when the product is not
  // linked. Lets the analysis dialog warn when the data feed is unhealthy.
  instrumentSync: InstrumentSyncHealth | null;
  // QL-706 — calendar statistics from the monthly series, on the same data as
  // the chart: in currencyIso, with the patch applied. null when the product is
  // not linked or its history cannot be calculated.
  historicalStatistics: HistoricalStatistics | null;
  // QL-736 — the date of the instrument's last stored price when it is more than
  // 45 days old: the chart, the expected return and volatility and the
  // statistics then end there instead of carrying that price to today. null
  // when they run to today.
  measuredTo: string | null;
};

export type ProductAllocationOverviewItem = {
  productId: string;
  nameI18n: LocalizedString;
  isin: string | null;
  allocations: Array<{
    assetClassId: string;
    assetClassNameI18n: LocalizedString;
    weight: number;
  }>;
};

// --- Cost Type Category Enum ---

export enum CostTypeCategory {
  RECURRING = "RECURRING",
  DEPOSIT = "DEPOSIT",
  WITHDRAWAL = "WITHDRAWAL",
}

// --- Cost Types & Portfolio Costs ---

export type CostType = {
  id: string;
  portfolioCollectionId: string;
  nameI18n: LocalizedString;
  category: string;
  apiKeys: string;
  /** QL-20: whether an advisor may override this fee in a flow instance. */
  advisorOverride: CostOverrideMode;
  order: number;
  createdAt: string;
  updatedAt: string;
};

export type PortfolioCostBracket = {
  id: string;
  startValue: number;
  value: number;
  order: number;
};

export type PortfolioCost = {
  id: string;
  portfolioCollectionId: string;
  nameI18n: LocalizedString;
  costType: string;
  useBrackets: boolean;
  category: "RECURRING" | "DEPOSIT" | "WITHDRAWAL";
  // Manual flat fee: a single percentage with an optional minimum fee amount,
  // no brackets, no API-key sync. When isManual is false this is a classic
  // bracket-based portfolio cost and valuePct/minFeeAmount are null.
  isManual: boolean;
  valuePct: number | null;
  minFeeAmount: number | null;
  // Optional mapping (manual fees): null = applies to any client category /
  // product cost class; otherwise the fee only applies when the context matches.
  clientCostCategoryId: string | null;
  productCostClassId: string | null;
  /** QL-20: whether an advisor may override this fee in a flow instance. */
  advisorOverride: CostOverrideMode;
  order: number;
  brackets: PortfolioCostBracket[];
  createdAt: string;
  updatedAt: string;
};

// --- Asset Classes & Correlations ---

export type AssetClass = {
  id: string;
  portfolioCollectionId: string;
  nameI18n: LocalizedString;
  order: number;
  ticker: string | null;
  dataset: string | null;
  expectedReturn: number | null;
  expectedVolatility: number | null;
  /** GAP-028 — how equity-like the class is, percent 0–100 (null = 0). */
  equitySharePct: number | null;
  createdAt: string;
  updatedAt: string;
  _count?: {
    productAllocations: number;
  };
};

/**
 * QL-462 — body of POST /internal/portfolio-collections/:id/asset-classes.
 * The instrument link is `ticker` + `dataset`, set together or not at all
 * (`""`, `null` and omitted all mean "no link").
 */
export type CreateAssetClassRequest = {
  nameI18n: LocalizedString;
  ticker?: string | null;
  dataset?: string | null;
  expectedReturn?: number;
  expectedVolatility?: number;
  /** GAP-028 — equity-likeness percent 0–100. */
  equitySharePct?: number;
};

/**
 * Body of PATCH /internal/portfolio-collections/:id/asset-classes/:acid. A full
 * replace, like create: an optional field left out is cleared, not kept.
 */
export type UpdateAssetClassRequest = CreateAssetClassRequest;

export type CorrelationMatrixData = {
  [fromAssetClassId: string]: {
    [toAssetClassId: string]: number | null;
  };
};

// --- Product Types ---

export type ProductType = {
  id: string;
  portfolioCollectionId: string;
  nameI18n: LocalizedString;
  order: number;
  isActive: boolean;
  /**
   * Whether this is the collection's default product type — at most one per
   * collection, always an active type. Set only by the toggle-default
   * operation; the create/update payloads do not accept it.
   */
  isDefault: boolean;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  /**
   * The suitability assessment modes enabled on this type, quiz first. Empty
   * means "Simple" — no assessment, a plain yes/no experience check. Populated
   * by the list query; absent on create/update responses (treated as "Simple").
   */
  assessmentModes?: ProductTypeAssessmentMode[];
};

// --- Goal Types (QP-187) ---
// Per-collection goal types rendered as the goal_input type picker. The row id
// is the stable identifier stored on goal.goalType; `iconSvg` is raw SVG
// markup (admin-entered).

export type PortfolioCollectionGoalType = {
  id: string;
  portfolioCollectionId: string;
  labelI18n: LocalizedString;
  iconSvg: string | null;
  /** QL-36 — client types that may pick this goal type; null = all. */
  allowedClientTypes: string[] | null;
  /** QL-375 — whether a goal of this type uses a mandate: hidden / required. */
  mandateMode: MandateMode;
  order: number;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * Slim goal-type shape shipped inside flow-definition responses (advisor + public).
 *
 * QL-36: the list is already filtered to the instance's client type by the
 * time it reaches either renderer. `allowedClientTypes` rides along on the
 * advisor payload (the settings UI reads it) and is stripped from the public
 * one, which has no use for tenant configuration.
 */
export type GoalTypeOption = {
  id: string;
  labelI18n: LocalizedString;
  iconSvg: string | null;
  allowedClientTypes?: string[] | null;
  /**
   * QL-36 — false when this client type may not pick the goal type, and the row
   * is only present because a goal in this instance already holds it. The picker
   * renders it for that one goal and hides it everywhere else. Absent = available.
   */
  available?: boolean;
  order: number;
};

/**
 * QL-36 — goal-type availability gate. Null/empty = open to all client types.
 * Delegates to the shared `clientTypeAllowed` so it cannot drift from the
 * flow-level gate it mirrors.
 */
export function clientTypeAllowedForGoalType(
  allowedClientTypes: string[] | null | undefined,
  clientType: string | null | undefined,
): boolean {
  return clientTypeAllowed(allowedClientTypes, clientType);
}

export type CreateGoalTypeRequest = {
  labelI18n: LocalizedString;
  iconSvg?: string | null;
  /** Omitted or null = available to every client type. */
  allowedClientTypes?: string[] | null;
  /** QL-375 — omitted = `hidden`. Only meaningful while the collection has mandates enabled. */
  mandateMode?: MandateMode;
};

export type UpdateGoalTypeRequest = CreateGoalTypeRequest;

export type ReorderGoalTypesRequest = {
  orderedIds: string[];
};

// --- Platforms (QP-195) ---
// A platform is a named filter preset inside one portfolio collection. It
// constrains which model portfolios and products a goal may use, selected per
// goal by goalType × derived statuses, and gives the selection a name for the PDF.

/** One eligibility condition: the goal's derived status `statusKey` must be one of `values`. */
export type PlatformCondition = {
  statusKey: string;
  values: string[];
};

export type PortfolioCollectionPlatform = {
  id: string;
  portfolioCollectionId: string;
  labelI18n: LocalizedString;
  /** Goal-type row ids this platform applies to. Empty = any goal type. */
  goalTypes: string[];
  /** Conditions on derived statuses. Null = any client. */
  conditions: PlatformCondition[] | null;
  /** Product row ids that are members of this platform. Empty = no products. */
  productIds: string[];
  order: number;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
};

/** Slim platform shape shipped to flow surfaces (advisor + public). */
export type PlatformOption = {
  id: string;
  labelI18n: LocalizedString;
  order: number;
};

export type CreatePlatformRequest = {
  labelI18n: LocalizedString;
  goalTypes?: string[];
  conditions?: PlatformCondition[] | null;
  /** Desired product membership. Undefined = empty on create / unchanged on update. */
  productIds?: string[];
};

export type UpdatePlatformRequest = CreatePlatformRequest;

/**
 * One blocking pair for a platform-membership save: removing `productId` from
 * the platform would break `portfolioId`, which is assigned to the platform
 * and still contains the product. A dynamic portfolio has no name — it is
 * identified by its `riskScore` and `portfolioNameI18n` is empty.
 */
export type PlatformMembershipCollision = {
  productId: string;
  productNameI18n: LocalizedString;
  portfolioKind: "model" | "dynamic";
  portfolioId: string;
  portfolioNameI18n: LocalizedString;
  /** Set for `portfolioKind: "dynamic"`. */
  riskScore?: number;
};

/**
 * 422 body from platform create/update when product removals would break
 * model or dynamic portfolios assigned to the platform. Discriminate from other 422s by
 * the presence of `collisions`.
 */
export type PlatformMembershipErrorResponse = {
  error: string;
  collisions: PlatformMembershipCollision[];
  /** QL-375 — mandate templates tagged to the platform that still list a removed product. */
  templateCollisions?: MandateTemplateMembershipCollision[];
};

export type ReorderPlatformsRequest = {
  orderedIds: string[];
};

/**
 * Per-goal platform state for the goal_input widget (advisor + public).
 * `resolved` is false when the goal type is unset or a platform condition
 * references a derived status the client does not have yet.
 */
export type GoalPlatformStateResponse = {
  /** Whether the flow's collection has any platforms configured. */
  hasPlatforms: boolean;
  /** Platforms currently eligible for this goal (goalType × statuses). */
  eligible: PlatformOption[];
  resolved: boolean;
  /** The goal's saved platform row id (may be stale/ineligible until synced). */
  selectedId: string | null;
};

export type SetGoalPlatformRequest = {
  platformId: string;
  lang?: string;
};

export type SetGoalPlatformResponse = {
  success: boolean;
  error?: string;
  refreshHints?: SnapshotInvalidation[];
  /** Names of custom products removed because the new platform excludes them. */
  removedProducts?: string[];
  /** QL-72 — model portfolios whose hand pick the new platform does not offer (cleared). */
  resetModelSelections?: string[];
};

// --- Client Preferences (QP-224) ---
// Per-collection client preference definitions answered by the client during a
// flow. A preference is a name and nothing else. No order column — lists sort
// by creation date.

export type ClientPreference = {
  id: string;
  portfolioCollectionId: string;
  nameI18n: LocalizedString;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateClientPreferenceRequest = {
  nameI18n: LocalizedString;
};

export type UpdateClientPreferenceRequest = CreateClientPreferenceRequest;

/**
 * Per-product boolean values keyed by client preference id. A missing key is
 * equivalent to `false`; the server materializes join rows only for `true`
 * values. In product payloads `undefined` means "leave untouched" (same
 * convention as `assetAllocations`).
 */
export type ProductClientPreferenceValues = Record<string, boolean>;

// --- Product Cost Classes ---
// Per-collection product classification used for COST mapping (brokerage class /
// fund type). Separate from ProductType (suitability).
export type ProductCostClass = {
  id: string;
  portfolioCollectionId: string;
  nameI18n: LocalizedString;
  order: number;
  isActive: boolean;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
};

// --- Wrapper Types ---
// Per-collection account wrapper types (ASK, IPS, …). Products declare which
// wrappers they may be held in via an ordered preference list; order 0 is the
// preferred wrapper when accounts are created on flow completion (later slice).
export type WrapperType = {
  id: string;
  portfolioCollectionId: string;
  nameI18n: LocalizedString;
  /**
   * Identifier matching this wrapper type when syncing external products —
   * case-sensitive exact match, unique per collection.
   */
  syncKey: string;
  isActive: boolean;
  /**
   * Minimum value an account of this wrapper type must hold after a flow's
   * one-time orders, in the collection currency (whole units, > 0).
   * Null = no minimum.
   */
  minAccountValue: number | null;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateWrapperTypeInput = {
  nameI18n: LocalizedString;
  syncKey: string;
  /** Omitted = no minimum on create / untouched on update; null clears it. */
  minAccountValue?: number | null;
};

export type UpdateWrapperTypeInput = CreateWrapperTypeInput;

// --- Instrument Search ---

export type InstrumentSearchResult = {
  ticker: string;
  name: string;
  isin: string;
  currency: string;
  exchange?: string;
};

export type SearchInstrumentsRequest = {
  searchTerm: string;
  datasets?: string[];
  page?: number;
  pageSize?: number;
};

export type SearchInstrumentsResponse = {
  results: InstrumentSearchResult[];
  total: number;
  // paged=true when the server applied page/pageSize (provider supports pagination);
  // paged=false when the server returned everything and the UI should slice client-side.
  paged: boolean;
};

// --- Asset Class Statistics ---

/** QL-462 — body of POST /internal/portfolio-collections/:id/asset-classes/statistics. */
export type AssetClassStatisticsRequest = {
  /** Dataset slug as stored on the asset class (`custom`, `msfunds`, `eodhd`, …). */
  dataset: string;
  ticker: string;
  /** Target currency for the series; defaults to NOK server-side. */
  currencyIso?: string;
  /** Risk-free rate in percent; defaults to 2.5 server-side. */
  riskFreeRatePct?: number;
};

export type AssetClassStatistics = {
  expectedReturn: number;
  expectedVolatility: number;
};

// --- Correlation Fetch Result ---

export type CorrelationFetchResult = {
  matrix: CorrelationMatrixData;
  instrumentIdToAssetClassId: Record<string, string>;
};

// --- Portfolio Total Weight ---

export type PortfolioTotalWeight = {
  totalWeight: number;
};

// --- Product Documents (per-product view) ---

export type ProductDocumentEntry = {
  id: string;
  /** Section locale (application locale code) the document is attached under. */
  language: string;
  source: string;
  /** Actual Morningstar language ID of the fetched document (may differ from `language`). */
  msLanguage: string | null;
  msDocumentDate: string | null;
  msDownloadUrl: string | null;
  uploadedFileName: string | null;
  uploadedFileUrl: string | null;
  lastSyncedAt: string | null;
};

export type ProductDocumentTypeWithDocuments = {
  id: string;
  nameI18n: LocalizedString;
  msDocumentTypeId: string | null;
  msMarket: string | null;
  isActive: boolean;
  documents: ProductDocumentEntry[];
};

// --- Bulk Product Import (CSV) ---

export type ProductImportRow = { source: string; id: string };

export type ProductImportRowStatus = "pending" | "success" | "failed" | "skipped";

export type ProductImportJobStatus = "pending" | "processing" | "done" | "failed";

export type ProductImportRowResult = {
  source: string;
  id: string;
  status: ProductImportRowStatus;
  productId?: string;
  error?: string;
};

export type CreateProductImportRequest = { rows: ProductImportRow[] };

export type CreateProductImportResponse = { jobId: string; totalRows: number };

export type ProductImportJob = {
  id: string;
  status: ProductImportJobStatus;
  totalRows: number;
  processedRows: number;
  successRows: number;
  failedRows: number;
  rows: ProductImportRowResult[];
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
};
